/* global Backbone, Marionette, _, define, jQuery, nfConversationalForms, nfRadio */
/* eslint-disable no-var, object-shorthand */
/**
 * Front-end conversational runtime.
 *
 * This view wraps Ninja Forms' own form content view rather than replacing
 * it: every field keeps its normal view, validation, and submission path, and
 * this layer only decides which fields are on screen, how the visitor moves
 * between them, and how the whole thing is painted. That wrapping is why the
 * view has to adapt to three different data shapes (see initialize) and why
 * it hides steps with `hidden`/`inert` instead of detaching them — a detached
 * field would drop out of validation and out of the submitted data.
 *
 * What is left here is the part that owns the DOM: rendering the shell,
 * deciding which step is on screen and what the controls around it say, and
 * moving between steps. The behavior wrapped around that lives beside it, and
 * is delegated to rather than reached into:
 *
 * - `models/steps`      — what counts as a step, for each data shape.
 * - `models/theme`      — what the appearance settings resolve to.
 * - `controllers/takeover`  — the takeover, the inert page behind it, and its
 *                             exits.
 * - `controllers/keyboard`  — keyboard operation, choice shortcuts, and when a
 *                             step advances by itself.
 * - `controllers/focus`     — what receives focus on arrival, and what that
 *                             element carries with it.
 * - `controllers/answers`   — what has been answered, and whether it passes.
 * - `controllers/fit`       — scaling a step down so it fits the viewport.
 *
 * When the form has the conversational display switched off, the view renders
 * a pass-through template and does nothing else.
 */
define( 'views/conversational',[
	'models/steps',
	'models/theme',
	'controllers/takeover',
	'controllers/keyboard',
	'controllers/focus',
	'controllers/answers',
	'controllers/fit',
], function (
	cfSteps,
	cfTheme,
	TakeoverController,
	KeyboardController,
	cfFocus,
	cfAnswers,
	FitController
) {
	/**
	 * Label a button without replacing the node the pointer is on.
	 *
	 * Navigation is refreshed from the `change` event, which a mouse fires
	 * between mousedown and mouseup — in the middle of the click on the very
	 * button being relabelled. `jQuery.text()` empties the element and appends
	 * a new text node, so the node under the pointer at mousedown is gone by
	 * mouseup; WebKit then cancels the click outright, which is why the first
	 * Continue click after typing an answer did nothing in Safari while the
	 * second worked. Chrome resolves the click to the surviving ancestor and
	 * hides the problem.
	 *
	 * So the label is written the two ways that leave the node alone: not at
	 * all when it already reads correctly, and in place when it does not. The
	 * full replacement is kept only for the first render, where there is no
	 * text node yet to write into.
	 *
	 * @param {Object} button The button to label.
	 * @param {string} label  What it should say.
	 * @return {void}
	 */
	function labelButton( button, label ) {
		var node = button.get( 0 );

		if ( ! node || node.textContent === label ) {
			return;
		}

		var text = node.firstChild;

		if ( text && text === node.lastChild && 3 === text.nodeType ) {
			text.nodeValue = label;
			return;
		}

		button.text( label );
	}

	/**
	 * The priority this add-on's content view filter is registered at.
	 *
	 * Must match `controllers/formContent.js`, which does the registering.
	 *
	 * @type {number}
	 */
	var CONTENT_VIEW_PRIORITY = 0;

	/**
	 * Render the wrapped view with this add-on absent from the filter list.
	 *
	 * Ninja Forms keeps content view filters in one array indexed by priority,
	 * and an add-on that renders another add-on's view leaves that array open
	 * to being read from inside its own render. Multi-Part does exactly that,
	 * and reads it by POSITION rather than by priority — `_.without( filters,
	 * undefined )[ 1 ]`, documented there as "the one with the highest
	 * priority after MP Forms". That holds for every add-on shipped so far,
	 * because all of them register after it. This add-on is the first to
	 * register ahead of it, at priority 0, which shifts that position by one
	 * and hands Multi-Part *itself* — so it renders a second copy of itself
	 * into its own body, without the part collection that copy needs, and the
	 * TypeError it throws aborts Ninja Forms' entire front-end render. The
	 * visitor sees an empty form, on any form, whether or not it has parts.
	 *
	 * So the list is restored to what it looked like before this add-on
	 * existed for exactly as long as the wrapped view is rendering. Anything
	 * counting positions in there counts the way it always did, and this holds
	 * for any such add-on, not only Multi-Part.
	 *
	 * Deliberately not fixed by declining to wrap: Multi-Part hands every form
	 * at least one part, so wrapping it is correct and the flow needs it. The
	 * positional read is Multi-Part's own latent defect — it carries the same
	 * pattern for load filters, where nothing triggers it today — and is worth
	 * repairing there. This keeps visitors' forms working on the Multi-Part
	 * versions already installed, which no fix shipped there can do.
	 *
	 * @param {Function} render Renders the wrapped view.
	 * @return {void}
	 */
	function withFilterWithdrawn( render ) {
		var filters = nfRadio
			.channel( 'formContent' )
			.request( 'get:viewFilters' );

		if ( ! filters ) {
			render();
			return;
		}

		var own = filters[ CONTENT_VIEW_PRIORITY ];
		// Left as a hole rather than spliced out: core indexes this array by
		// priority, so removing an element would renumber every filter after
		// it — the same class of bug being worked around here.
		filters[ CONTENT_VIEW_PRIORITY ] = undefined;

		try {
			render();
		} finally {
			filters[ CONTENT_VIEW_PRIORITY ] = own;
		}
	}

	var view = Marionette.LayoutView.extend( {
		className: 'nf-cf',

		regions: {
			content: '.nf-cf__content',
		},

		events: {
			'click .nf-cf__start': 'start',
			'click .nf-cf__close': 'closeFullScreen',
			'click .nf-cf__back': 'previous',
			'click .nf-cf__next': 'next',
			'input .nf-element': 'updateNavigation',
			// The view owns the elements these are bound to; what they mean
			// belongs to the keyboard controller, so they are handed straight
			// over rather than given a method here to travel through.
			'change .nf-element': function ( event ) {
				this.keyboard.onChange( event );
			},
			'keydown .nf-cf__canvas': function ( event ) {
				this.keyboard.onKeydown( event );
			},
			'mousedown .nf-cf__canvas': function () {
				this.keyboard.onPointerDown();
			},
		},

		/**
		 * Detect the form's data shape and wire the matching listeners.
		 *
		 * Ninja Forms hands the form content view one of three data shapes,
		 * and the conversational flow adapts to each rather than normalizing
		 * them:
		 *
		 * - Multi-part (the Multi-Part Forms add-on): a collection that can
		 *   report its visible parts. Where the owner made more than one part,
		 *   each part is one step — a part is a group of questions the visitor
		 *   answers on one screen — and stepping is delegated to that add-on
		 *   so its own state stays authoritative. A form with a single part is
		 *   not a divided form at all, whatever the data shape says, so it
		 *   steps question by question like any other. Part and visibility
		 *   changes are listened to either way.
		 * - Layout/row (the Layout & Styles add-on): a collection of rows of
		 *   cells. Each row that holds at least one non-structural field is
		 *   one step.
		 * - Flat field list (core): each visible non-structural field is one
		 *   step.
		 *
		 * Field visibility changes (conditional logic) are listened to in all
		 * three shapes, as is the form's submit response, which is what adds
		 * the close control to the completion message in full screen.
		 *
		 * When the display is switched off for this form, only the
		 * pass-through template is chosen and no listeners are attached.
		 *
		 * @param {Object} options           View options.
		 * @param {Object} options.formModel The Ninja Forms form model.
		 * @param {Object} options.data      Form content data in one of the
		 *                                   three shapes above.
		 * @return {void}
		 */
		initialize: function ( options ) {
			this.formModel = options.formModel;
			this.data = options.data;
			this.enabled = this.setting( 'conversational_enabled', false );
			this.template = this.enabled
				? '#tmpl-nf-cf-shell'
				: '#tmpl-nf-cf-pass-through';
			this.currentIndex = 0;
			this.steps = [];
			this.started = false;
			// Neither of these is read until the visitor is moving through the
			// flow, but every property this view remembers is declared here on
			// purpose: someone auditing the view's state should be able to
			// read it, not grep for assignments buried in later methods.
			this.positionText = '';
			this.isMultiPart =
				this.data && 'function' === typeof this.data.getVisibleParts;
			// Whether parts are what the visitor steps through, which is a
			// different question from whether Multi-Part is rendering the
			// form. That add-on wraps EVERY form in a part once it is active,
			// including forms whose owner never divided anything, so treating
			// "has parts" as "is a multi-part form" would put a whole
			// six-question form on one screen and call it step 1 of 1. A form
			// only becomes multi-part in the sense the visitor experiences
			// when its owner made a second part. Below that, the parts
			// collection still renders the form and still owns its own state
			// — only the stepping is the fields' again.
			this.partsAreSteps = Boolean(
				this.isMultiPart && 1 < this.data.length
			);
			this.isLayout =
				! this.isMultiPart &&
				cfSteps.collectionLooksLikeRows( this.data );

			// The one thing every module is given: a lookup scoped to this
			// view's own element, so nothing beside it has to know where the
			// form was rendered or go looking through the document for it.
			this.lookup = this.$.bind( this );

			this.takeover = new TakeoverController( {
				el: this.$el,
				lookup: this.lookup,
				boundary: this.formWrapper.bind( this ),
				hasUnsentAnswers: this.hasUnsentAnswers.bind( this ),
			} );

			this.fit = new FitController( {
				lookup: this.lookup,
				isOn: function () {
					return this.enabled;
				}.bind( this ),
			} );

			this.keyboard = new KeyboardController( {
				activeStep: this.activeStepElement.bind( this ),
				currentStep: this.currentStep.bind( this ),
				isStarted: function () {
					return this.started;
				}.bind( this ),
				advance: this.next.bind( this ),
				refreshNavigation: this.updateNavigation.bind( this ),
			} );

			if ( this.enabled && this.formModel.get( 'fields' ) ) {
				this.listenTo(
					this.formModel.get( 'fields' ),
					'change:visible',
					this.onVisibilityChange
				);
			}

			if ( this.enabled ) {
				this.listenTo(
					nfRadio.channel( 'form-' + this.formModel.get( 'id' ) ),
					'submit:response',
					this.onSubmitResponse
				);
			}

			if ( this.enabled && this.isMultiPart ) {
				this.listenTo(
					this.data,
					'change:part change:visible',
					this.onPartChange
				);
			}
		},

		/**
		 * Supply the shell template's labels and welcome-screen content.
		 *
		 * Every label falls back from the form's own setting to the
		 * translated default, so an empty setting never renders a blank
		 * button.
		 *
		 * @return {Object} Template values.
		 */
		templateHelpers: function () {
			return {
				formID: this.formModel.get( 'id' ),
				returnLabel: nfConversationalForms.i18n.returnToSite,
				progressMode: this.setting( 'conversational_progress', 'bar' ),
				logo: cfTheme.mediaUrl(
					this.setting( 'conversational_logo', '' )
				),
				welcomeHeading:
					this.setting( 'conversational_welcome_heading', '' ) ||
					this.setting( 'title', '' ),
				welcomeText: this.setting( 'conversational_welcome_text', '' ),
				startLabel:
					this.setting( 'conversational_start_label', '' ) ||
					nfConversationalForms.i18n.start,
				backLabel:
					this.setting( 'conversational_back_label', '' ) ||
					nfConversationalForms.i18n.back,
				continueLabel:
					this.setting( 'conversational_continue_label', '' ) ||
					nfConversationalForms.i18n.continue,
			};
		},

		/**
		 * Show the wrapped form content and open the conversational flow.
		 *
		 * The underlying Ninja Forms view is always rendered first, so the
		 * form works normally even when the display is off — in that case the
		 * view only marks itself as pass-through and stops.
		 *
		 * Otherwise the theme is applied, the steps are built, and the form
		 * either opens on the welcome screen or starts immediately. A
		 * launcher is also required when the form is configured to take over
		 * the screen but is embedded in a page, since something has to
		 * trigger that takeover.
		 *
		 * @return {void}
		 */
		onRender: function () {
			var UnderlyingView = this.getUnderlyingView();
			var options = {
				data: this.data,
				formModel: this.formModel,
			};

			if ( this.data instanceof Backbone.Collection ) {
				options.collection = this.data;
			} else if ( this.data instanceof Backbone.Model ) {
				options.model = this.data;
			}

			var region = this.content;
			withFilterWithdrawn( function () {
				region.show( new UnderlyingView( options ) );
			} );

			if ( ! this.enabled ) {
				this.$el.addClass( 'nf-cf--pass-through' );
				return;
			}

			this.applyTheme();
			this.refreshSteps();

			var welcomeEnabled = Boolean(
				Number( this.setting( 'conversational_welcome', 1 ) )
			);
			var requiresLauncher =
				Boolean(
					Number( this.setting( 'conversational_full_screen', 0 ) )
				) && ! nfConversationalForms.publicForm;

			if ( nfConversationalForms.publicForm ) {
				this.takeover.enter();
			}

			// `started` survives a re-render because the view instance does,
			// and it is the guard against re-opening on the welcome screen
			// halfway through the conversation. Ninja Forms re-renders the
			// form content view while a submission is in flight, and without
			// this the welcome heading and Start button reappeared over the
			// visitor's last answer for as long as the submission took.
			if ( ! this.started && ( welcomeEnabled || requiresLauncher ) ) {
				this.showWelcome();
			} else {
				this.start();
			}
		},

		/**
		 * Re-apply the theme once the view is in the document.
		 *
		 * The Ninja Forms wrappers this view lives inside only exist in the
		 * document, so both the active-form marker and the theme's custom
		 * properties have to be set again here — at onRender time those
		 * ancestors are not reachable.
		 *
		 * The takeover has the same dependency: a public form page enters it
		 * during render, when there is no page around the view yet to make
		 * inert, so that work is finished here.
		 *
		 * @return {void}
		 */
		onAttach: function () {
			if ( this.enabled ) {
				this.wrapperChain().addClass( 'nf-cf-form-active' );
				this.applyTheme();
				this.takeover.ensureBackgroundInert();
			}
		},

		/**
		 * The Ninja Forms wrapper this view was rendered inside.
		 *
		 * Named once because it is the view's boundary in three different
		 * senses: the element the takeover must not make inert, the parent the
		 * success message is a sibling under, and the start of the chain the
		 * view decorates.
		 *
		 * @return {jQuery} The form wrapper, empty until the view is attached.
		 */
		formWrapper: function () {
			return this.$el.closest( '.nf-form-wrap' );
		},

		/**
		 * The elements outside this view that it decorates.
		 *
		 * The active-form marker, the theme's custom properties, and the
		 * cleanup on destroy all act on this same pair of ancestors, so the
		 * chain is defined once: a caller that assembled it again and forgot
		 * the outer container would leave a marker behind on a destroyed view,
		 * with nothing left to remove it.
		 *
		 * @return {jQuery} The form wrapper and its container.
		 */
		wrapperChain: function () {
			var wrapper = this.formWrapper();

			return wrapper.add( wrapper.closest( '.nf-form-cont' ) );
		},

		/**
		 * Pick the Ninja Forms content view this view should wrap.
		 *
		 * The view filters are keyed by priority, and the ones used here are
		 * the same ones Ninja Forms would have chosen for this data shape:
		 * multi-part, layout/row, or the default field list.
		 *
		 * @return {Function} The view constructor to render inside the shell.
		 */
		getUnderlyingView: function () {
			var filters = nfRadio
				.channel( 'formContent' )
				.request( 'get:viewFilters' );
			var callback;

			if ( this.isMultiPart && filters[ 1 ] ) {
				callback = filters[ 1 ];
			} else if ( this.isLayout && filters[ 4 ] ) {
				callback = filters[ 4 ];
			} else {
				callback = filters[ 10 ];
			}

			return callback();
		},

		/**
		 * Read one of the form's settings.
		 *
		 * Empty, null, and undefined all fall back, because a form saved
		 * before a setting existed carries no value for it and a cleared
		 * field carries an empty string.
		 *
		 * @param {string} name     Setting name.
		 * @param {*}      fallback Value to use when the setting is unset.
		 * @return {*} The stored value, or the fallback.
		 */
		setting: function ( name, fallback ) {
			var settings = this.formModel.get( 'settings' ) || {};
			var value = settings[ name ];

			return 'undefined' === typeof value ||
				null === value ||
				'' === value
				? fallback
				: value;
		},

		/**
		 * The step the visitor is on.
		 *
		 * @return {Object|undefined} The current step, if there is one.
		 */
		currentStep: function () {
			return this.steps[ this.currentIndex ];
		},

		/**
		 * Rebuild the step list and keep the visitor where they were.
		 *
		 * Called on every visibility change, so conditional logic can add or
		 * remove steps mid-flow. The step the visitor is on is remembered by
		 * key and looked up again in the new list, because a step that
		 * appeared or disappeared above them would otherwise shift them to a
		 * different question. The index is finally clamped in case their step
		 * is the one that just disappeared.
		 *
		 * @return {void}
		 */
		refreshSteps: function () {
			var currentKey = this.steps[ this.currentIndex ]
				? this.steps[ this.currentIndex ].key
				: null;

			var lookup = this.lookup;

			if ( this.partsAreSteps ) {
				this.steps = _.map(
					this.data.getVisibleParts(),
					function ( part ) {
						return cfSteps.partStep( part, lookup );
					}
				);
				this.currentIndex = Math.max(
					0,
					this.data
						.getVisibleParts()
						.indexOf( this.data.getElement() )
				);
			} else if ( this.isMultiPart ) {
				// One part: step through what is inside it, which is the
				// whole form. Its content has its own shape, and which
				// elements `update` hides depends on that, so it is recorded
				// rather than re-derived there.
				var onlyPart = this.data.getVisibleParts()[ 0 ];
				var partContent = onlyPart
					? onlyPart.get( 'formContentData' )
					: null;
				this.stepsAreRows =
					cfSteps.collectionLooksLikeRows( partContent );
				this.steps = partContent
					? cfSteps.contentSteps( partContent, lookup )
					: [];
			} else if ( this.isLayout ) {
				this.steps = cfSteps.layoutSteps( this.data, lookup );
			} else {
				this.steps = cfSteps.fieldSteps( this.data, lookup );
			}

			// Hold the visitor's place across a rebuild — but only where this
			// view is the one keeping it. When parts are the steps, Multi-Part
			// owns which part is current, and the index derived from it above
			// is the answer; re-matching the key held before the rebuild would
			// silently undo the move that caused it, leaving the counter on
			// the part the visitor just left while the next part's questions
			// are on screen.
			if ( currentKey && ! this.partsAreSteps ) {
				var matchingIndex = _.findIndex( this.steps, function ( step ) {
					return step.key === currentKey;
				} );
				if ( -1 !== matchingIndex ) {
					this.currentIndex = matchingIndex;
				}
			}

			this.currentIndex = Math.min(
				this.currentIndex,
				Math.max( 0, this.steps.length - 1 )
			);
			this.update();
		},

		/**
		 * Show only the current step and resync everything around it.
		 *
		 * Off-screen steps are hidden in place rather than removed, and are
		 * marked `hidden`, `inert`, and `aria-hidden` together so they are
		 * invisible, untabbable, and unannounced while their fields stay part
		 * of the form. Multi-part is excluded from this: that add-on already
		 * renders one part at a time.
		 *
		 * Navigation, progress, and choice shortcuts are then rebuilt, and
		 * focus is moved — but only once the visitor has started, so the
		 * welcome screen is not stolen from.
		 *
		 * @return {void}
		 */
		update: function () {
			if ( ! this.enabled || ! this.steps.length ) {
				return;
			}

			if ( ! this.partsAreSteps ) {
				var candidates =
					this.isLayout || this.stepsAreRows
						? this.$( '.nf-row' )
						: this.$( '.nf-field-container' );
				candidates
					.prop( 'hidden', true )
					.prop( 'inert', true )
					.attr( 'aria-hidden', 'true' )
					.removeClass( 'nf-cf__step--active' );

				_.each(
					this.steps,
					function ( step, index ) {
						if ( ! step.el ) {
							return;
						}

						var active = index === this.currentIndex;
						step.el.hidden = ! active;
						step.el.inert = ! active;
						step.el.setAttribute(
							'aria-hidden',
							active ? 'false' : 'true'
						);
						jQuery( step.el ).toggleClass(
							'nf-cf__step--active',
							active
						);
					},
					this
				);
			}

			this.updateNavigation();
			this.updateProgress();
			this.keyboard.decorateShortcuts();
			// Last, and on a frame of its own: the step has to be laid out at
			// its natural size before there is anything true to measure.
			this.fit.schedule();

			if ( this.started ) {
				this.focusCurrentStep();
			}
		},

		/**
		 * Set the back/next controls for the current step.
		 *
		 * Back is hidden on the first step, and next is hidden on the last one,
		 * where the form's own submit button takes over. Steps that advance
		 * themselves keep their continue button: a visitor browsing the
		 * choices with the arrow keys does not advance on selection, so
		 * without it there would be no way off the step from the keyboard.
		 *
		 * The next button says "skip" instead of "continue" while a step the
		 * visitor could answer is optional and unanswered, so skipping is
		 * offered rather than implied. A step that asks nothing keeps
		 * "continue": there is no answer to pass over.
		 *
		 * @return {void}
		 */
		updateNavigation: function () {
			// A step's height is not settled once it has been rendered: a
			// validation message appears under a field the moment the visitor
			// leaves it empty, a character count grows, a description wraps to
			// another line. This runs on every input and change, so it is
			// where a step that has just grown gets re-fitted; the request is
			// coalesced to one measurement per frame.
			this.fit.schedule();

			var step = this.steps[ this.currentIndex ];
			var isLast = this.currentIndex === this.steps.length - 1;
			var answered = cfAnswers.isAnswered(
				step,
				this.activeStepElement()
			);
			var required = cfAnswers.isRequired( step );
			// Skip is only honest where there is something to skip. A step
			// that collects no answer — a statement the visitor reads and
			// moves on from — can never become answered, so it would wear the
			// Skip label permanently and offer to pass over a question it
			// never asked.
			var answerable = _.some( step.fields, function ( field ) {
				return cfSteps.collectsAnswer( field );
			} );
			this.$( '.nf-cf__back' ).prop( 'hidden', 0 === this.currentIndex );

			var next = this.$( '.nf-cf__next' ).prop( 'hidden', isLast );

			labelButton(
				next,
				answerable && ! required && ! answered
					? this.setting(
							'conversational_skip_label',
							nfConversationalForms.i18n.skip
					  )
					: this.setting(
							'conversational_continue_label',
							nfConversationalForms.i18n.continue
					  )
			);

			this.$( '.nf-cf__submit' ).toggle( isLast );

			if ( isLast ) {
				this.placeSubmitButton();
			}
		},

		/**
		 * Move the form's own submit button into the last step.
		 *
		 * The button belongs to Ninja Forms and is only re-parented, so the
		 * normal submission path is untouched. It is also un-hidden here
		 * because update() hides it along with the rest of its step.
		 *
		 * The control itself is given an explicit tabindex for the same
		 * reason the conversational controls carry one in the template:
		 * Safari leaves submit inputs out of the tab order under the macOS
		 * Keyboard navigation default, and on the last step this control is
		 * the only way to send the form. It is set here rather than in the
		 * template because the control is core's, and it is set every time
		 * because core re-renders the field.
		 *
		 * @return {void}
		 */
		placeSubmitButton: function () {
			var submit = this.$( '.submit-container' ).first();

			if ( submit.length && ! submit.parent().is( '.nf-cf__submit' ) ) {
				this.$( '.nf-cf__submit' ).append( submit );
			}

			submit
				.prop( 'hidden', false )
				.prop( 'inert', false )
				.attr( 'aria-hidden', 'false' )
				.find( 'input, button' )
				.attr( 'tabindex', '0' );
		},

		/**
		 * Update the progress bar, the counter, and the position text.
		 *
		 * The same "question X of Y" text is written into a screen-reader-only
		 * position element as well as the visible counter, because the bar's
		 * width means nothing to a screen reader. The position element is
		 * deliberately not a live region: every step change also moves focus,
		 * and a screen reader flushes a queued polite announcement when the
		 * focus change lands, so a live write here is never heard. The text is
		 * carried by whatever receives focus instead — see controllers/focus.
		 *
		 * @return {void}
		 */
		updateProgress: function () {
			var completed = this.currentIndex + 1;
			var percent = this.steps.length
				? ( completed / this.steps.length ) * 100
				: 0;
			var count = nfConversationalForms.i18n.questionCount
				.replace( '%1$s', this.currentIndex + 1 )
				.replace( '%2$s', this.steps.length );

			this.positionText = count;
			this.$( '.nf-cf__progress-value' ).css( 'width', percent + '%' );
			this.$( '.nf-cf__progress-count' ).text( count );

			// The screen-reader copy is spoken immediately before the
			// question, so it ends in a full stop: without one the two run
			// together as a single sentence. The visible counter above is
			// left as it reads on screen.
			this.$( '.nf-cf__position' ).text( count + '.' );
		},

		/**
		 * Show the welcome screen and hold the flow before the first step.
		 *
		 * @return {void}
		 */
		showWelcome: function () {
			this.started = false;
			this.$el.removeClass( 'nf-cf--started' );
			this.$( '.nf-cf__welcome' ).prop( 'hidden', false );
			this.$(
				'.nf-cf__content, .nf-cf__navigation, .nf-cf__progress'
			).prop( 'hidden', true );
		},

		/**
		 * Begin the flow: swap the welcome screen for the first question.
		 *
		 * Marking the flow as started is what allows update() to move focus,
		 * so the visitor lands on the first question instead of having to
		 * find it. The takeover is entered here when the form asks for it,
		 * because a takeover triggered before the visitor acted would be
		 * hijacking the page.
		 *
		 * @return {void}
		 */
		start: function () {
			this.started = true;
			this.$el.addClass( 'nf-cf--started' );
			this.$( '.nf-cf__welcome' ).prop( 'hidden', true );
			this.$(
				'.nf-cf__content, .nf-cf__navigation, .nf-cf__progress'
			).prop( 'hidden', false );

			if (
				nfConversationalForms.publicForm ||
				Number( this.setting( 'conversational_full_screen', 0 ) )
			) {
				this.takeover.enter();
			}

			this.update();
		},

		/**
		 * Leave full screen, or leave the page when there is nothing behind.
		 *
		 * On a dedicated conversational form page the form is the page, so
		 * closing means going home; embedded forms return to the welcome
		 * screen and hand focus back to the control that opened them.
		 *
		 * @return {void}
		 */
		closeFullScreen: function () {
			if ( this.takeover.exit( true ) ) {
				return;
			}

			this.takeover.close();
			this.showWelcome();
			this.$( '.nf-cf__start' ).trigger( 'focus' );
		},

		/**
		 * Whether the visitor has entered something that leaving would lose.
		 *
		 * Asked by the takeover before it leaves a page that would discard
		 * the answers with it.
		 *
		 * @return {boolean} True when answers exist that were never sent.
		 */
		hasUnsentAnswers: function () {
			return cfAnswers.hasUnsentAnswers(
				this.steps,
				this.activeStepElement(),
				this.started
			);
		},

		/**
		 * Hand the visitor over to the completion message.
		 *
		 * After a successful submission Ninja Forms replaces the form with its
		 * success message, and the flow has to follow the visitor there. The
		 * message is not a control, so nothing is focused by the swap: the
		 * panel is made programmatically focusable and focused, which both
		 * tells a screen reader where it now is and keeps the keyboard inside
		 * the message instead of stranding it on the document.
		 *
		 * The focused panel is announced from its own text — the success
		 * message it already contains. It deliberately gets no live region:
		 * the same write-then-focus race that silenced the step arrivals
		 * would swallow a polite announcement here too, and the panel's text
		 * is the whole message anyway.
		 *
		 * In full screen the takeover also gives the message the close control
		 * it has no way of providing itself — without one there is no exit.
		 *
		 * @param {Object} response The submission response.
		 * @return {void}
		 */
		onSubmitResponse: function ( response ) {
			var message =
				response && response.data && response.data.actions
					? response.data.actions.success_message
					: '';
			var panel = this.formWrapper().children( '.nf-response-msg' );

			if ( ! message || ! panel.length ) {
				return;
			}

			this.takeover.decorateCompletion( panel );

			panel.attr( 'tabindex', '-1' ).trigger( 'focus' );
		},

		/**
		 * Advance to the next step once the current one validates.
		 *
		 * Multi-part stepping is delegated to that add-on so its own part
		 * state and validation stay authoritative.
		 *
		 * @return {void}
		 */
		next: function () {
			if ( ! cfAnswers.validateStep( this.currentStep(), this.lookup ) ) {
				return;
			}

			if ( this.partsAreSteps ) {
				this.data.next();
				return;
			}

			if ( this.currentIndex < this.steps.length - 1 ) {
				this.currentIndex += 1;
				this.update();
			}
		},

		/**
		 * Go back one step.
		 *
		 * Going back is never validated: the visitor is allowed to leave a
		 * question unanswered on the way back to fix an earlier one.
		 *
		 * @return {void}
		 */
		previous: function () {
			if ( this.partsAreSteps ) {
				this.data.previous();
				return;
			}

			if ( this.currentIndex > 0 ) {
				this.currentIndex -= 1;
				this.update();
			}
		},

		/**
		 * Find the element holding the current step.
		 *
		 * Falls back to the multi-part body, which is the step container when
		 * that add-on is rendering the form.
		 *
		 * @return {jQuery} The current step's element.
		 */
		activeStepElement: function () {
			var step = this.steps[ this.currentIndex ];
			return step && step.el
				? jQuery( step.el )
				: this.$( '.nf-mp-body' );
		},

		/**
		 * Move focus to the new step so the flow reads as one conversation.
		 *
		 * The step's position travels with whatever receives focus, so the
		 * element carrying that text is handed over with it — see
		 * controllers/focus for why nothing may be announced any other way.
		 *
		 * @return {void}
		 */
		focusCurrentStep: function () {
			cfFocus.focusStep(
				this.activeStepElement(),
				this.positionText,
				this.$( '.nf-cf__position' ).first().attr( 'id' )
			);
		},

		/**
		 * Rebuild the steps when conditional logic shows or hides a field.
		 *
		 * @return {void}
		 */
		onVisibilityChange: function () {
			this.refreshSteps();
		},

		/**
		 * Follow the multi-part add-on when it changes part.
		 *
		 * Parts can also be shown or hidden by conditional logic, so the step
		 * list is rebuilt rather than just re-indexed.
		 *
		 * @return {void}
		 */
		onPartChange: function () {
			// Deferred because two listeners answer the same `change:part`,
			// and this one is registered first: this view exists before the
			// wrapped view it creates, so its handler runs before Multi-Part
			// has replaced the part body's contents. Refreshing inline would
			// therefore read and decorate markup that is about to be thrown
			// away — the arriving choices kept their plain core appearance,
			// with no A/B shortcut badge and no selected state, until the
			// visitor clicked one and a second refresh happened to run
			// against the markup that had by then replaced it. Focus lands on
			// a doomed element for the same reason. Deferring puts this after
			// the swap, which is the only ordering that can be relied on
			// without reaching into another add-on's internals.
			_.defer( this.refreshSteps.bind( this ) );
		},

		/**
		 * Paint the form's resolved theme onto its wrappers.
		 *
		 * The values come from models/theme; this only decides which elements
		 * carry them. All three levels are set because the canvas, the Ninja
		 * Forms wrapper, and the outer container can each be the element a
		 * given rule matches against.
		 *
		 * @return {void}
		 */
		applyTheme: function () {
			var properties = cfTheme.properties(
				this.setting.bind( this ),
				nfConversationalForms.defaults || {},
				cfTheme.mediaUrl
			);

			this.$el.add( this.wrapperChain() ).each( function () {
				_.each(
					properties,
					function ( value, name ) {
						this.style.setProperty( name, value );
					},
					this
				);
			} );
		},

		/**
		 * Undo everything this view put outside its own element.
		 *
		 * The wrapper markers are the view's own; the full-screen body class
		 * and the inert background are the takeover's, and it is asked to
		 * release them. Leaving any of them behind is worse than untidy: the
		 * page would stay unscrollable and, without the background restore,
		 * permanently untabbable and unreadable to assistive technology, with
		 * no view left to undo it.
		 *
		 * @return {void}
		 */
		onBeforeDestroy: function () {
			this.takeover.release();
			this.wrapperChain().removeClass( 'nf-cf-form-active' );
		},
	} );

	return view;
} );

